import { createReducer } from '@reduxjs/toolkit';
import { TState } from './types';
import {
  asyncOperationsSnapshotReceived,
  asyncOperationsTopicFollowed,
  asyncOperationUpdated,
  awaitAsyncOperation,
  clearAsyncOperations,
  finishedOperationsHandled,
  setRealtimeConnectionStatus,
  setTopicSubscriptionError,
  topicDataChanged,
} from './actions';
import { applyOperationUpdate, applySnapshot, awaitOperation, forgetTopic } from './utils';
import { getTopicKey } from '../../../api/realtime/helpers';

const initialState: TState = {
  operationsById: {},
  connectionStatus: 'Disconnected',
  topicDataChangedAt: {},
  subscriptionErrors: {},
  awaitedOperations: {},
  finishedOperationIds: [],
};

export const asyncOperationsReducer = createReducer(initialState, builder =>
  builder
    .addCase(asyncOperationUpdated, (state, { payload }) => {
      return applyOperationUpdate(state, payload);
    })
    .addCase(asyncOperationsSnapshotReceived, (state, { payload }) => {
      return applySnapshot(state, payload);
    })
    .addCase(awaitAsyncOperation, (state, { payload }) => {
      return awaitOperation(state, payload.operationId, payload.target);
    })
    .addCase(finishedOperationsHandled, (state, { payload }) => {
      return {
        ...state,
        finishedOperationIds: state.finishedOperationIds.filter(id => !payload.includes(id)),
      };
    })
    .addCase(asyncOperationsTopicFollowed, (state, { payload }) => {
      return forgetTopic(state, payload);
    })
    .addCase(topicDataChanged, (state, { payload }) => {
      return {
        ...state,
        topicDataChangedAt: {
          ...state.topicDataChangedAt,
          [getTopicKey(payload)]: payload.changedAtUtc,
        },
      };
    })
    .addCase(setRealtimeConnectionStatus, (state, { payload }) => {
      return { ...state, connectionStatus: payload };
    })
    .addCase(setTopicSubscriptionError, (state, { payload }) => {
      const { [payload.topicKey]: previous, ...subscriptionErrors } = state.subscriptionErrors;
      if (!payload.errorCode) {
        return previous === undefined ? state : { ...state, subscriptionErrors };
      }
      return {
        ...state,
        subscriptionErrors: { ...subscriptionErrors, [payload.topicKey]: payload.errorCode },
      };
    })
    .addCase(clearAsyncOperations, state => {
      return {
        ...state,
        operationsById: {},
        topicDataChangedAt: {},
        subscriptionErrors: {},
        awaitedOperations: {},
        finishedOperationIds: [],
      };
    })
);
